import React, { useState, FormEvent } from 'react';
import authService from '../services/authService';
import './Login.css';

interface RegisterProps {
  onRegister: (token: string, user: any, restaurants?: any[]) => void;
  onBackToLogin: () => void;
}

const Register: React.FC<RegisterProps> = ({ onRegister, onBackToLogin }) => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validações
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (!restaurantName.trim()) {
      setError('Nome do restaurante é obrigatório');
      return;
    }

    setLoading(true);

    try {
      // Remove formatação do telefone (mantém apenas números)
      const cleanPhone = phone.replace(/\D/g, '');
      const phoneWithCountryCode = `+55${cleanPhone}`;

      const response = await authService.register({
        phone: phoneWithCountryCode,
        password,
        restaurantName: restaurantName.trim(),
        email: email.trim() || undefined,
      });

      onRegister(response.token, response.user, response.restaurants);
    } catch (err: any) {
      console.error('Register error:', err);
      setError(err.response?.data?.message || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  return (
    <div className="login-page">
      <div className="login-container register-container">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-icon">✓</span>
            <span className="login-logo-text">FidHub Admin</span>
          </div>
          <h1>Criar Conta</h1>
          <p>Cadastre seu restaurante no programa de fidelidade</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="restaurantName">Nome do Restaurante *</label>
            <input
              id="restaurantName"
              type="text"
              placeholder="Ex: Pizzaria Bella Napoli"
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Número de Telefone *</label>
            <div className="phone-input-group">
              <span className="phone-prefix">+55</span>
              <input
                id="phone"
                type="tel"
                placeholder="(11) 98765-4321"
                value={phone}
                onChange={handlePhoneChange}
                maxLength={15}
                required
                disabled={loading}
              />
            </div>
            <small className="form-hint">Este será seu usuário de acesso</small>
          </div>

          <div className="form-group">
            <label htmlFor="email">E-mail (opcional)</label>
            <input
              id="email"
              type="email"
              placeholder="contato@restaurante.com.br"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha *</label>
            <input
              id="password"
              type="password"
              placeholder="Mínimo 6 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              disabled={loading}
            />
            <small className="form-hint">Senha deve ter no mínimo 6 caracteres</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Senha *</label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Digite a senha novamente"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Criando conta...
              </>
            ) : (
              'Criar Conta'
            )}
          </button>

          <div className="login-footer">
            <button 
              type="button" 
              onClick={onBackToLogin} 
              className="back-to-login-btn"
              disabled={loading}
            >
              ← Voltar para o login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;


